import { useState, useEffect } from "react"
import { Send, Plus, TrendingUp, TrendingDown, Users, DollarSign, Eye, EyeOff, Trash2, Globe } from "lucide-react"
import api from "../services/api"
import Badge from "../components/common/Badge"
import { formatCurrency, formatPct, formatRelative } from "../utils/formatters"
import { PageLoader } from "../components/common/LoadingSpinner"
import toast from "react-hot-toast"

const SYMBOLS = ["BTC/USDT","ETH/USDT","SOL/USDT","BNB/USDT","XRP/USDT","ADA/USDT","AVAX/USDT","DOT/USDT"]

function CreateModal({ onClose, onCreate }) {
  const [form, setForm] = useState({ title: "", symbol: "BTC/USDT", direction: "long", entry_price: "", stop_loss: "", take_profit: "", description: "" })
  const [saving, setSaving] = useState(false)
  async function submit(e) {
    e.preventDefault()
    setSaving(true)
    try {
      const payload = { ...form, entry_price: parseFloat(form.entry_price), stop_loss: form.stop_loss ? parseFloat(form.stop_loss) : null, take_profit: form.take_profit ? parseFloat(form.take_profit) : null }
      const { data } = await api.post("/signals/", payload)
      toast.success("Signal created!")
      onCreate(data)
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create signal") }
    finally { setSaving(false) }
  }
  const rr = form.entry_price && form.stop_loss && form.take_profit ? Math.abs((parseFloat(form.take_profit) - parseFloat(form.entry_price)) / (parseFloat(form.entry_price) - parseFloat(form.stop_loss))).toFixed(2) : null
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 w-full max-w-md">
        <h3 className="font-bold text-white text-lg mb-4">Create Signal</h3>
        <form onSubmit={submit} className="space-y-3">
          <div><label className="label">Title</label><input className="input" value={form.title} onChange={e => setForm({...form, title: e.target.value})} required placeholder="e.g. BTC Breakout Long"/></div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="label">Symbol</label>
              <select className="input" value={form.symbol} onChange={e => setForm({...form, symbol: e.target.value})}>
                {SYMBOLS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div><label className="label">Direction</label>
              <select className="input" value={form.direction} onChange={e => setForm({...form, direction: e.target.value})}>
                <option value="long">Long</option>
                <option value="short">Short</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><label className="label">Entry Price</label><input type="number" step="any" className="input" value={form.entry_price} onChange={e => setForm({...form, entry_price: e.target.value})} required/></div>
            <div><label className="label">Stop Loss</label><input type="number" step="any" className="input" value={form.stop_loss} onChange={e => setForm({...form, stop_loss: e.target.value})}/></div>
            <div><label className="label">Take Profit</label><input type="number" step="any" className="input" value={form.take_profit} onChange={e => setForm({...form, take_profit: e.target.value})}/></div>
          </div>
          {rr && <p className="text-xs text-trading-green">Risk/Reward: 1:{rr}</p>}
          <div><label className="label">Description</label><textarea className="input" rows={3} value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Analysis and rationale..."/></div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary flex-1">{saving ? "Saving..." : "Create Signal"}</button>
          </div>
        </form>
      </div>
    </div>
  )
}

function SignalCard({ signal, onPublish, onDelete }) {
  const isLong = signal.direction === "long" || signal.signal_type === "buy"
  const rr = signal.entry_price && signal.stop_loss && signal.take_profit ? Math.abs((signal.take_profit - signal.entry_price) / (signal.entry_price - signal.stop_loss)).toFixed(2) : null
  return (
    <div className="card">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className={`p-1.5 rounded-lg ${isLong ? "bg-trading-green/10" : "bg-trading-red/10"}`}>
              {isLong ? <TrendingUp size={13} className="text-trading-green"/> : <TrendingDown size={13} className="text-trading-red"/>}
            </div>
            <h3 className="font-bold text-white">{signal.title}</h3>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Badge variant="blue">{signal.symbol}</Badge>
            <Badge variant={isLong ? "green" : "red"}>{signal.direction || signal.signal_type}</Badge>
            <Badge variant={signal.is_published ? "green" : "gray"}>{signal.is_published ? "Published" : "Draft"}</Badge>
          </div>
        </div>
        <div className="flex gap-1.5">
          <button onClick={() => onPublish(signal)} className={`p-1.5 rounded-lg ${signal.is_published ? "bg-gray-500/10 text-gray-400 hover:bg-gray-500/20" : "bg-brand-500/10 text-brand-400 hover:bg-brand-500/20"}`} title={signal.is_published ? "Unpublish" : "Publish"}>
            {signal.is_published ? <EyeOff size={13}/> : <Globe size={13}/>}
          </button>
          <button onClick={() => onDelete(signal.id)} className="p-1.5 rounded-lg bg-trading-red/10 text-trading-red hover:bg-trading-red/20"><Trash2 size={13}/></button>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        {[["Entry", signal.entry_price ? formatCurrency(signal.entry_price) : "—", "text-white"], ["Stop Loss", signal.stop_loss ? formatCurrency(signal.stop_loss) : "—", "text-trading-red"], ["Take Profit", signal.take_profit ? formatCurrency(signal.take_profit) : "—", "text-trading-green"]].map(([k,v,c]) => (
          <div key={k} className="bg-surface-100 rounded-xl p-2 text-center">
            <p className="text-[10px] text-gray-500">{k}</p>
            <p className={`text-xs font-mono font-semibold ${c}`}>{v}</p>
          </div>
        ))}
      </div>
      {rr && <p className="text-xs text-trading-green mb-2">R/R: 1:{rr}</p>}
      {signal.description && <p className="text-xs text-gray-400 mb-3 line-clamp-2">{signal.description}</p>}
      <div className="flex items-center justify-between text-xs text-gray-500 border-t border-surface-200 pt-2">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1"><Users size={11}/>{signal.followers_count ?? 0}</span>
          <span className="flex items-center gap-1"><DollarSign size={11}/>${(signal.monthly_revenue ?? 0).toFixed(0)}/mo</span>
        </div>
        <span>{formatRelative(signal.created_at)}</span>
      </div>
    </div>
  )
}

export default function Signals() {
  const [signals, setSignals] = useState([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [filter, setFilter] = useState("all")

  async function load() {
    try {
      const { data } = await api.get("/signals/my")
      setSignals(data)
    } catch { setSignals([]) }
    finally { setLoading(false) }
  }
  useEffect(() => { load() }, [])

  async function togglePublish(signal) {
    try {
      if (signal.is_published) {
        await api.patch(`/signals/${signal.id}/unpublish`)
        toast.success("Signal unpublished")
      } else {
        await api.patch(`/signals/${signal.id}/publish`)
        toast.success("Signal published to marketplace!")
      }
      await load()
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update signal") }
  }

  async function deleteSignal(id) {
    if (!confirm("Delete this signal?")) return
    await api.delete(`/signals/${id}`)
    toast.success("Signal deleted")
    setSignals(s => s.filter(x => x.id !== id))
  }

  const filtered = filter === "all" ? signals : filter === "published" ? signals.filter(s => s.is_published) : signals.filter(s => !s.is_published)
  const totalFollowers = signals.reduce((s, sig) => s + (sig.followers_count ?? 0), 0)
  const totalRevenue = signals.reduce((s, sig) => s + (sig.monthly_revenue ?? 0), 0)

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">My Signals</h2>
          <p className="text-sm text-gray-400 mt-1">Create and publish trading signals to the marketplace</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary flex items-center gap-2 text-sm"><Plus size={15}/> New Signal</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[["Signals", signals.length, "text-white"], ["Published", signals.filter(s => s.is_published).length, "text-trading-green"], ["Followers", totalFollowers, "text-brand-400"], ["Revenue", `$${totalRevenue.toFixed(0)}/mo`, "text-trading-yellow"]].map(([k,v,c]) => (
          <div key={k} className="card-sm text-center"><p className="text-xs text-gray-400 mb-1">{k}</p><p className={`text-xl font-bold ${c}`}>{v}</p></div>
        ))}
      </div>
      <div className="flex gap-2">
        {["all","published","draft"].map(f => (
          <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${filter === f ? "bg-brand-500 text-white" : "text-gray-400 hover:text-white bg-surface-100"}`}>{f}</button>
        ))}
      </div>
      {loading ? <PageLoader/> : filtered.length === 0 ? (
        <div className="card text-center py-16">
          <Send size={40} className="mx-auto text-gray-600 mb-3"/>
          <h3 className="font-semibold text-white mb-2">No signals yet</h3>
          <p className="text-gray-400 text-sm mb-4">Create your first signal to share with the marketplace</p>
          <button onClick={() => setShowCreate(true)} className="btn-primary inline-flex items-center gap-2"><Plus size={14}/> Create Signal</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(s => <SignalCard key={s.id} signal={s} onPublish={togglePublish} onDelete={deleteSignal}/>)}
        </div>
      )}
      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onCreate={sig => { setSignals(ss => [sig, ...ss]); setShowCreate(false) }}/>}
    </div>
  )
}
